/**
 * disclaimer.ts — the Terms of Use & Disclaimer shown before entering FFR Video
 * Chat, plus its version. MUST stay in lock-step with server/src/disclaimer.ts
 * (`DISCLAIMER_VERSION`): bump both together whenever the text below changes so
 * users are re-prompted and a fresh consent record is written.
 *
 * NOT legal advice — have counsel review before production (recording-consent
 * laws, biometric/COPPA/GDPR/CCPA, and a linked Privacy Policy).
 */
export const DISCLAIMER_VERSION = 1;

export const DISCLAIMER_TITLE = 'FlashFlashRevolution Video Chat — Terms of Use & Disclaimer';

export const DISCLAIMER_INTRO =
    'FFR Video Chat lets registered members share live webcam video, microphone audio, and text with other users. ' +
    'These streams are created by users in real time and are NOT pre-screened. By entering, you agree to the terms below. ' +
    'If you do not agree, do not enter.';

export const DISCLAIMER_SECTIONS: Array<{ h: string; p?: string; list?: string[] }> = [
    {
        h: '1. Age requirement',
        p: 'You must be at least 13 years old to use FFR Video Chat. By entering, you affirm that you are 13 or older. '
            + 'If you are under 18, you confirm you have permission from a parent or guardian.',
    },
    {
        h: '2. Enter at your own risk',
        p: 'This is a live, user-driven space. You may be exposed to content, language, or behavior from other users that is '
            + 'unexpected, offensive, or inappropriate, even though it violates our rules. You use FFR Video Chat voluntarily and at your own risk.',
    },
    {
        h: '3. Recording & automated monitoring',
        p: 'For safety and moderation, your video, audio, and text in FFR Video Chat may be recorded, stored, and reviewed by FFR staff. '
            + 'We may also use automated, computer-assisted detection to analyze streams and flag or warn about inappropriate or harmful behavior. '
            + 'Moderators may watch or join any room. By entering, you consent to this recording and monitoring and understand you have no '
            + 'expectation of privacy in the chat.',
    },
    {
        h: '4. Data we collect & how it is used',
        p: 'We may collect and retain your account identifier (or, for guests, your IP address), connection timestamps, text messages, '
            + 'recorded audio/video, and automated-detection results. We use this to operate the service, keep users safe, moderate, prevent '
            + 'abuse, and enforce these terms, and we may disclose information to law enforcement where required by law. See the FFR Privacy Policy for details.',
    },
    {
        h: '5. Rules of conduct — while using FFR Video Chat you must NOT:',
        list: [
            'use profanity, slurs, or hate speech;',
            'show, say, or share anything sexually explicit, violent, or otherwise inappropriate;',
            'harass, threaten, bully, or impersonate anyone;',
            'share illegal content or encourage illegal activity;',
            "share others' personal information, or record/re-share another user's stream without their consent;",
            'broadcast anything you do not have the right to share.',
        ],
    },
    {
        h: '6. Your responsibilities',
        p: 'You are solely responsible for everything you transmit — video, audio, and text. Do not show or say anything you would not want '
            + 'recorded, seen by moderators, or made public. Protect your own privacy and personal safety, and only share content you have the right to share.',
    },
    {
        h: '7. Enforcement',
        p: 'Breaking these rules may result in a warning, removal from a room, or a temporary or permanent ban — at FFR’s sole discretion and without notice.',
    },
    {
        h: '8. No warranty & limitation of liability',
        p: 'FFR Video Chat is provided "as is," without warranties of any kind. To the fullest extent permitted by law, FlashFlashRevolution and its '
            + 'operators are not responsible or liable for user conduct, user content, or any harm, loss, or damage arising from your use of the service. '
            + 'You agree to use it at your own risk and to indemnify and hold FFR harmless from claims arising out of your use or your content.',
    },
    {
        h: '9. Record of acceptance',
        p: 'Your acceptance of these terms is logged with a timestamp and your account identifier (or, for guests, your IP address) as a record of consent.',
    },
    {
        h: '10. Changes',
        p: 'We may update these terms; if they change, you may be asked to accept again. Continued use means you accept the current terms.',
    },
];

export const DISCLAIMER_AGREE_LABEL =
    'I am at least 13 years old, and I have read and agree to the Terms of Use & Disclaimer above.';
